import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { pushBackHandler } from '../utils/backStack';

const IS_NATIVE = (() => { try { return Capacitor.isNativePlatform(); } catch (_) { return false; } })();

/**
 * Instagram-style history-back modal closer — StrictMode-safe.
 *
 * When `open` becomes true we push a dummy history entry (unless one is
 * already on top of the stack — this guard makes the hook idempotent
 * under React 18 StrictMode's double-invocation of effects).  Any back
 * navigation (browser back, Android hardware back, iOS swipe-back) then
 * fires `popstate`, which we intercept to invoke `onClose` — closing the
 * modal one step at a time without leaving the underlying page.
 *
 * IMPORTANT: this hook does NOT call `history.back()` inside cleanup.
 * Doing so races with StrictMode's mount→cleanup→mount sequence and
 * causes modals to auto-close.  If the modal is closed programmatically
 * (e.g. tapping a member to open their profile), navigate with
 * `{ replace: true }` so the dummy history entry is overwritten cleanly:
 *
 *     navigate(`/user/${email}`, { replace: true });
 *
 * If instead you close via a UI button (rare — most flows now rely on
 * device back), simply call `window.history.back()` from the click
 * handler; that fires popstate → onClose → the effect cleans up.
 */
export const useHistoryClose = (open, onClose) => {
  const cbRef = useRef(onClose);
  cbRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    // Android app: hardware back goes through the in-memory back stack
    // (no history sentinels → never a "dead" back press).
    if (IS_NATIVE) return pushBackHandler(() => cbRef.current && cbRef.current());

    // Only push a sentinel entry if one isn't already on top of the
    // stack.  This prevents double-push under StrictMode where the
    // effect body runs twice back-to-back.
    if (!(window.history.state && window.history.state.__modal)) {
      window.history.pushState({ __modal: Date.now() }, '');
    }

    const handler = () => cbRef.current && cbRef.current();
    window.addEventListener('popstate', handler);

    return () => {
      window.removeEventListener('popstate', handler);
    };
  }, [open]);
};

/* Android-app-only back handler (no browser history interplay on web). */
export const useNativeBack = (active, onBack) => {
  const cbRef = useRef(onBack);
  cbRef.current = onBack;
  useEffect(() => {
    if (!active || !IS_NATIVE) return undefined;
    return pushBackHandler(() => cbRef.current && cbRef.current());
  }, [active]);
};

export default useHistoryClose;
